/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import type { ReportType } from "~/generated/graphql";
import { dateFormat } from "~/constants/dateFormats";
import { createCountFilterDefinition } from "~/hooks/useAppliedFilters/definitionFactories";
import { AppliedFilterDefinition } from "~/hooks/useAppliedFilters/types";
import {
  getCountBasedDisplayValue,
  removeSelectedValue,
} from "~/hooks/useAppliedFilters/utils";
import { QueryState } from "~/hooks/useQueryState/useQueryState";
import { ReportsQuery } from "~/views/resources/types";
import { getReportTypeLabel } from "./reportTypeLabels";

type DefinitionKey =
  | "dateGenerated"
  | "reportStatuses"
  | "reportTypes"
  | "createdBy";

type ReportsQueryState = ReportsQuery & QueryState;

type Definition = AppliedFilterDefinition<ReportsQueryState, DefinitionKey>;

const reportStatusLabelsByValue: Record<string, string> = {
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  FAILED: "Failed",
};

export const reportsAppliedFilters: Definition[] = [
  {
    key: "dateGenerated",
    label: "Date Generated",
    enableValuePopover: false,
    getValues: (queryState) =>
      queryState.dateGeneratedStart && queryState.dateGeneratedEnd
        ? [queryState.dateGeneratedStart, queryState.dateGeneratedEnd]
        : [],
    getValueDisplay: (queryState) =>
      `${dayjs(queryState.dateGeneratedStart).format(
        dateFormat.shortDate
      )} - ${dayjs(queryState.dateGeneratedEnd).format(dateFormat.shortDate)}`,
    clear: () => ({
      dateGeneratedStart: undefined,
      dateGeneratedEnd: undefined,
    }),
  },
  {
    key: "reportStatuses",
    label: "Report Status",
    getValues: (queryState) => queryState.reportStatuses ?? [],
    getValueLabel: (value) => reportStatusLabelsByValue[value] ?? value,
    getValueDisplay: (queryState, values) => {
      if (values.length === 1) {
        const label = reportStatusLabelsByValue[values[0]] ?? values[0];
        return queryState.excludeReportStatuses ? `Exclude: ${label}` : label;
      }

      return getCountBasedDisplayValue(
        values,
        queryState.excludeReportStatuses,
        {
          singular: "Report Status",
          plural: "Report Statuses",
        }
      );
    },
    clear: () => ({
      reportStatuses: undefined,
      excludeReportStatuses: undefined,
    }),
    removeValue: (queryState, valueToRemove) => {
      const nextValues = removeSelectedValue(
        queryState.reportStatuses,
        valueToRemove
      );
      return {
        reportStatuses: nextValues.length ? nextValues : undefined,
        excludeReportStatuses: nextValues.length
          ? queryState.excludeReportStatuses
          : undefined,
      };
    },
  },
  {
    key: "reportTypes",
    label: "Report Type",
    getValues: (queryState) => queryState.reportTypes ?? [],
    getValueLabel: (value) => getReportTypeLabel(value as ReportType),
    getValueDisplay: (queryState, values) => {
      if (values.length === 1) {
        const label = getReportTypeLabel(values[0] as ReportType);
        return queryState.excludeReportTypes ? `Exclude: ${label}` : label;
      }

      return getCountBasedDisplayValue(values, queryState.excludeReportTypes, {
        singular: "Report Type",
        plural: "Report Types",
      });
    },
    clear: () => ({ reportTypes: undefined, excludeReportTypes: undefined }),
    removeValue: (queryState, valueToRemove) => {
      const nextValues = removeSelectedValue(
        queryState.reportTypes,
        valueToRemove
      );
      return {
        reportTypes: nextValues.length ? nextValues : undefined,
        excludeReportTypes: nextValues.length
          ? queryState.excludeReportTypes
          : undefined,
      };
    },
  },
  createCountFilterDefinition<ReportsQueryState, "createdBy">({
    key: "createdBy",
    label: "Created By",
    labels: { singular: "Creator", plural: "Creators" },
    getValues: (queryState) => queryState.createdBy,
    getExcluded: (queryState) => queryState.excludeCreatedBy,
    clear: () => ({ createdBy: undefined, excludeCreatedBy: undefined }),
    applyValuesPatch: (queryState, values) => ({
      createdBy: values.length ? values : undefined,
      excludeCreatedBy: values.length ? queryState.excludeCreatedBy : undefined,
    }),
  }),
];
